import { useContext, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import toast from 'react-hot-toast';

import { AuthContext } from '../contexts/AuthContext';
import { LabourerContext } from '../contexts/LabourerContext';

import SectionHeader from './SectionHeader';
import Loader from './Loader';

import GeneralInformation from './UserProfile/GeneralInformation';
import EmergencyContact from './UserProfile/EmergencyContact';
import ContactDetails from './UserProfile/ContactDetails';
import SuperAnnuationAndTax from './UserProfile/SuperAnnuationAndTax';
import BankDetails from './UserProfile/BankDetails';
import PasswordReset from './UserProfile/PasswordReset';
import ConnectGoogleAccount from './UserProfile/ConnectGoogleAccount';

import labourerService from '../services/labourerService';
import authService from '../services/authService';

import { Squares2X2Icon, LifebuoyIcon, BanknotesIcon, BuildingLibraryIcon, PhoneIcon, LockClosedIcon, LinkIcon } from '@heroicons/react/24/outline';
import { ArrowLeftEndOnRectangleIcon } from '@heroicons/react/20/solid';

const LabourerProfile = () => {
  const { user, mutateUser, setToken } = useContext(AuthContext);
  const { labourer, mutateLabourer } = useContext(LabourerContext);

  const [isLoading, setIsLoading] = useState(false);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const steps = [
    {
      id: 'general-information',
      label: 'General information'
    },
    {
      id: 'contact-details',
      label: 'Contact details'
    },
    {
      id: 'password-reset',
      label: 'Password reset'
    },
    {
      id: 'emergency-contact',
      label: 'Emergency contact'
    },
    {
      id: 'google-connect',
      label: 'Google connect'
    },
    {
      id: 'super-annuation-tax',
      label: 'Super Annuation & Tax'
    },
    {
      id: 'bank-details',
      label: 'Bank details'
    }
  ];

  const StepIcon = ({ step }) => {
    switch (step) {
      case 'general-information':
        return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
      case 'emergency-contact':
        return <LifebuoyIcon className='h-5 w-5 text-ase-blue' />;
      case 'contact-details':
        return <PhoneIcon className='h-5 w-5 text-ase-blue' />;
      case 'google-connect':
        return <LinkIcon className='h-5 w-5 text-ase-blue' />;
      case 'password-reset':
        return <LockClosedIcon className='h-5 w-5 text-ase-blue' />;
      case 'super-annuation-tax':
        return <BanknotesIcon className='h-5 w-5 text-ase-blue' />;
      case 'bank-details':
        return <BuildingLibraryIcon className='h-5 w-5 text-ase-blue' />;
      default:
        return <Squares2X2Icon className='h-5 w-5 text-ase-blue' />;
    }
  }

  const [currentStep, setCurrentStep] = useState(steps.find(step => step.id === 'general-information'));

  const personalSteps = ['general-information', 'contact-details', 'password-reset', 'emergency-contact', 'google-connect'];
  const financeSteps = ['super-annuation-tax', 'bank-details'];

  useEffect(() => {
    const linkGoogleAccount = async () => {
      try {
        const code = searchParams.get('code');

        await authService.linkGoogleAccount({
          code,
          userId: user._id,
          redirectUri: `${process.env.REACT_APP_APP_URL}/l/profile`
        });

        mutateUser();

        toast.success('Google account linked');
      } catch (error) {
        toast.error('Something went wrong');
      } finally {
        setIsLinkingGoogle(false);
        setSearchParams({});
      }
    };

    if (searchParams.has('code') && user?._id) {
      if (!isLinkingGoogle) {
        setIsLinkingGoogle(true);

        linkGoogleAccount();
      }
    }
  }, [searchParams, isLinkingGoogle, mutateUser, user?._id, setSearchParams])

  const handleGeneralInformationSubmit = async (data) => {
    try {
      setIsLoading(true);

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          ...data
        }
      });

      mutateLabourer();
      mutateUser();

      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleContactDetailsSubmit = async (data) => {
    try {
      setIsLoading(true);

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          ...data
        }
      });

      mutateUser();

      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleEmergencyContactSubmit = async (data) => {
    try {
      setIsLoading(true);

      const { fullName, relationship, phoneNumber } = data;

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          emergencyContact: {
            fullName,
            relationship,
            phoneNumber
          }
        }
      });

      mutateLabourer();

      toast.success('General information updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleSuperAnnuationAndTaxSubmit = async (data) => {
    try {
      const { superAnnuation, taxFileNumber } = data;

      setIsLoading(true);

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          superAnnuation,
          taxFileNumber
        }
      });

      mutateLabourer();

      toast.success('Super Annuation & Tax updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleBankDetailsSubmit = async (data) => {
    try {
      setIsLoading(true);

      const { bankName, BSB, accountNumber, accountName } = data;

      await labourerService.updateLabourer({
        labourerId: labourer._id,
        updateData: {
          bankDetails: {
            bankName,
            BSB,
            accountNumber,
            accountName
          }
        }
      });

      mutateLabourer();

      toast.success('Bank details updated');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handlePasswordResetSubmit = async (data) => {
    try {
      setIsLoading(true);

      await authService.updatePassword({
        userId: user._id,
        password: data.password
      });

      mutateLabourer();

      toast.success('Password changed');
    } catch (error) {
      console.log(error);
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('token');
    setToken(null);
    mutateUser(null);
    mutateLabourer(null);
  }

  if (searchParams.has('code')) {
    return (
      <>
        <Helmet>
          <title>Profile | ASE Group</title>
        </Helmet>

        <div className='min-h-[80vh] flex flex-col gap-4 items-center justify-center'>
          <Loader />
          <p>Linking your Google account</p>
        </div>
      </>
    )
  }


  return (
    <>
      <Helmet>
        <title>Profile | ASE Group</title>
      </Helmet>

      <div className='flex flex-col md:flex-row justify-between w-full md:w-[1000px] mx-auto mb-10 gap-4 md:gap-8'>
        <div className='flex gap-4 bg-ase-blue md:hidden overflow-scroll p-2 rounded-md'>
          {steps.map(step => (
            <button
              onClick={() => setCurrentStep(step)}
              key={step.id}
              className={`${currentStep.id === step.id ? 'bg-white text-ase-blue' : 'bg-white text-black bg-opacity-65'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-xs w-full rounded-md text-nowrap`}
            >
              <StepIcon step={step.id} />
              <span>{step.label}</span>
            </button>
          ))}
        </div>

        <div className='hidden md:flex flex-col gap-4'>
          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Personal' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => personalSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <StepIcon step={step.id} />
                  <span>{step.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className='border-ase-blue border-x border-t rounded-lg h-fit w-[250px]'>
            <SectionHeader title='Finance' />

            <div className='bg-white p-2 border-b border-ase-blue rounded-b-lg'>
              {steps.filter(s => financeSteps.includes(s.id)).map(step => (
                <button
                  onClick={() => setCurrentStep(step)}
                  key={step.id}
                  className={`${currentStep.id === step.id ? 'bg-ase-blue text-ase-blue bg-opacity-10' : 'text-black'} px-2 flex items-center gap-2 hover:text-ase-blue py-2 text-sm w-full rounded-md`}
                >
                  <StepIcon step={step.id} />
                  <span>{step.label}</span>
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleLogout}
            className='bg-red-600 text-white font-semibold h-[36px] rounded-md w-[250px] flex items-center justify-center gap-1 text-sm'
          >
            <ArrowLeftEndOnRectangleIcon className='h-5 w-5' /> Sign out
          </button>
        </div>

        <div className='border border-ase-blue rounded-lg h-fit flex-grow bg-white pb-1'>
          <SectionHeader title={currentStep.label} />

          <div className='p-4'>
            {currentStep.id === 'general-information' && <GeneralInformation
              includeTitle={false}
              isLoading={isLoading}
              onSubmit={handleGeneralInformationSubmit}
            />}

            {currentStep.id === 'emergency-contact' && <EmergencyContact
              isLoading={isLoading}
              labourer={labourer}
              onSubmit={handleEmergencyContactSubmit}
            />}

            {currentStep.id === 'password-reset' && <PasswordReset
              isLoading={isLoading}
              onSubmit={handlePasswordResetSubmit}
            />}

            {currentStep.id === 'contact-details' && <ContactDetails
              isLoading={isLoading}
              onSubmit={handleContactDetailsSubmit}
            />}

            {currentStep.id === 'google-connect' && <ConnectGoogleAccount
              isLoading={isLoading}
              onSubmit={handleContactDetailsSubmit}
            />}

            {currentStep.id === 'super-annuation-tax' && <SuperAnnuationAndTax
              labourer={labourer}
              isLoading={isLoading}
              onSubmit={handleSuperAnnuationAndTaxSubmit}
            />}

            {currentStep.id === 'bank-details' && <BankDetails
              labourer={labourer}
              isLoading={isLoading}
              onSubmit={handleBankDetailsSubmit}
            />}
          </div>
        </div>
      </div>
    </>
  )
}

export default LabourerProfile;
